import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createCn } from '../../../helpers/utils';
import classes from '../styles.module.scss';
import { Attach, AiChatIcon, Calendar, Send, SpeakersIcon } from '../../../assets';
import { useTranslation } from 'react-i18next';
import { apiService, ChatStructuredResponse, MeetingInsights, StructuredTask, SummaryStructured, VideoScreenInsights } from '../../../services/api.service';
import { formatChatAnswer } from '../../../utils/sanitize-content';
import { highlightTechTerms } from '../../../utils/tech-terms';
import { markdownToHtml } from '../../../utils/markdown';
import { isKnownDeadline, isUrgentDeadline } from '../../../utils/parse-chat-response';
import { buildChatSuggestions, ChatSection } from './chat-suggestions';

interface IMessage {
  id: number;
  text: string;
  sender: 'user' | 'ai';
  structured?: ChatStructuredResponse | null;
}

type Props = {
  meetingId?: string;
  section?: ChatSection;
  hasProtocol?: boolean;
  hasTranscription?: boolean;
  meetingName?: string;
  insights?: MeetingInsights | null;
  summaryStructured?: SummaryStructured | null;
  videoScreenInsights?: VideoScreenInsights | null;
  structuredTasks?: StructuredTask[];
  speakers?: string[];
  pendingPrompt?: string;
  onPendingPromptConsumed?: () => void;
};

const cn = createCn('file', classes);

function StructuredChatAnswer({ data }: { data: ChatStructuredResponse }) {
  return (
    <div className={cn('chat-answer-bubble')}>
      {data.intro && (
        <p
          className={cn('chat-answer-intro')}
          dangerouslySetInnerHTML={{ __html: highlightTechTerms(data.intro) }}
        />
      )}

      {data.items.length > 0 && (
        <ul className={cn('chat-decision-list')}>
          {data.items.map(item => {
            const urgent = isUrgentDeadline(item.deadline, item.deadline_iso);
            return (
              <li key={item.num} className={cn('chat-decision-card')}>
                <div className={cn('chat-decision-num')}>{item.num}</div>
                <div className={cn('chat-decision-body')}>
                  <div className={cn('chat-decision-responsible')}>{item.responsible}</div>
                  <div
                    className={cn('chat-decision-text')}
                    dangerouslySetInnerHTML={{ __html: highlightTechTerms(item.text) }}
                  />
                  {isKnownDeadline(item.deadline) && (
                    <div className={cn('chat-decision-deadline', { urgent })}>
                      <Calendar />
                      <span>{item.deadline}</span>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {(data.sources.length > 0 || data.urgent_count > 0) && (
        <footer className={cn('chat-answer-footer')}>
          <span className={cn('chat-answer-footer-label')}>Источник:</span>
          <div className={cn('chat-answer-tags')}>
            {data.sources.map(source => (
              <span key={source} className={cn('chat-answer-tag')}>{source}</span>
            ))}
            {data.urgent_count > 0 && (
              <span className={cn('chat-answer-tag', { urgent: true })}>
                {data.urgent_count} срочных задач
              </span>
            )}
          </div>
        </footer>
      )}
    </div>
  );
}

function PlainChatAnswer({ text }: { text: string }) {
  const html = useMemo(() => {
    const prepared = formatChatAnswer(text);
    return highlightTechTerms(markdownToHtml(prepared));
  }, [text]);

  return (
    <div
      className={cn('chat-answer-bubble')}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function ChatMessageBody({ message }: { message: IMessage }) {
  if (message.sender === 'user') {
    return <div className={cn('chat-question-bubble')}>{message.text}</div>;
  }

  return (
    <div className={cn('chat-answer-row')}>
      <div className={cn('chat-avatar')} aria-hidden>
        <AiChatIcon />
      </div>
      {message.structured?.intro || message.structured?.items?.length
        ? <StructuredChatAnswer data={message.structured} />
        : <PlainChatAnswer text={message.text} />}
    </div>
  );
}

const FileChat = ({
  meetingId,
  section = 'general',
  hasProtocol = false,
  hasTranscription = false,
  meetingName,
  insights = null,
  summaryStructured = null,
  videoScreenInsights = null,
  structuredTasks = [],
  speakers = [],
  pendingPrompt,
  onPendingPromptConsumed,
}: Props) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [remoteSuggestions, setRemoteSuggestions] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const localSuggestions = useMemo(
    () => buildChatSuggestions({
      section,
      hasProtocol,
      hasTranscription,
      meetingName,
      insights,
      summaryStructured,
      videoScreenInsights,
      structuredTasks,
      speakers,
    }),
    [section, hasProtocol, hasTranscription, meetingName, insights, summaryStructured, videoScreenInsights, structuredTasks, speakers],
  );

  const suggestions = remoteSuggestions.length > 0 ? remoteSuggestions : localSuggestions;

  useEffect(() => {
    if (!meetingId) return;
    let cancelled = false;
    setRemoteSuggestions([]);
    apiService.getChatSuggestions(meetingId, section)
      .then(res => {
        if (!cancelled && res.suggestions?.length) {
          setRemoteSuggestions(res.suggestions);
        }
      })
      .catch(() => {
        /* fallback: localSuggestions */
      });
    return () => { cancelled = true; };
  }, [meetingId, section, insights, summaryStructured, hasProtocol, hasTranscription]);

  const handleSend = async (text?: string) => {
    const content = (text || query).trim();
    if (!content || loading) return;

    const userMsg: IMessage = {
      id: Date.now(),
      text: content,
      sender: 'user',
    };
    setMessages(prev => [...prev, userMsg]);
    setQuery('');
    setLoading(true);

    try {
      let answer = '';
      let structured: ChatStructuredResponse | null = null;

      if (meetingId) {
        const res = await apiService.chatWithMeeting(meetingId, content);
        answer = res.response?.trim() || 'Не удалось получить ответ.';
        structured = res.structured ?? null;
      } else if (!hasProtocol && !hasTranscription) {
        answer = 'Материалы встречи ещё не загружены. Дождитесь обработки аудио или сгенерируйте протокол.';
      } else {
        answer = 'Чат недоступен: не указан идентификатор встречи.';
      }

      setMessages(prev => [
        ...prev,
        { id: Date.now() + 1, text: answer, sender: 'ai', structured },
      ]);
    } catch (err) {
      setMessages(prev => [
        ...prev,
        {
          id: Date.now() + 1,
          text: err instanceof Error ? err.message : 'Ошибка при отправке сообщения',
          sender: 'ai',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    const prompt = pendingPrompt?.trim();
    if (!prompt || loading) return;
    handleSend(prompt);
    onPendingPromptConsumed?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPrompt]);

  return (
    <div className={cn('chat-wrapper')}>
      <div className={cn('chat-header')}>
        {t('navigation.ai-chat')}
      </div>

      <div className={cn('chat-body')}>
        <div className={cn('chat-messages')}>
          {messages.map(msg => (
            <div
              key={`${msg.sender}-${msg.id}`}
              className={cn('chat-message', { ai: msg.sender === 'ai', user: msg.sender === 'user' })}
            >
              <ChatMessageBody message={msg} />
            </div>
          ))}
          {loading && (
            <div className={cn('chat-message', { ai: true })}>
              <div className={cn('chat-answer-row')}>
                <div className={cn('chat-avatar')} aria-hidden>
                  <AiChatIcon />
                </div>
                <div className={cn('chat-answer-bubble')}>
                  <span className={cn('chat-answer-loading')}>Анализирую протокол и транскрипцию…</span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {messages.length === 0 && !loading && (
          <div className={cn('chat-suggestions')}>
            {suggestions.map(question => (
              <button key={question} type="button" onClick={() => handleSend(question)}>
                {question}
              </button>
            ))}
          </div>
        )}

        <div className={cn('chat-input-wrapper')}>
          <input
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Задайте вопрос о протоколе или транскрипции…"
            disabled={loading}
          />

          <div className={cn('chat-input-actions')}>
            <div className={cn('chat-input-action')}>
              <Attach />
            </div>
            <div className={cn('chat-input-action')}>
              <SpeakersIcon />
            </div>
            <div
              className={cn('chat-input-action', { disabled: loading || !query.trim() })}
              onClick={() => handleSend()}
            >
              <Send />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileChat;
